import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminRegistrarService } from "@/services/admin-registrar.service";

/**
 * Query keys for registrar settings admin queries.
 *
 * - ['admin', 'registrar'] — all registrar queries
 * - ['admin', 'registrar', 'settings'] — the settings query
 */
export const registrarKeys = {
  all: ["admin", "registrar"] as const,
  settings: () => [...registrarKeys.all, "settings"] as const,
};

/**
 * Get the active registrar provider configuration and connectivity indicator.
 *
 * @returns The registrar settings DTO
 */
export function useAdminRegistrarSettings() {
  return useQuery({
    queryKey: registrarKeys.settings(),
    queryFn: () => adminRegistrarService.getSettings(),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Run an on-demand registrar connectivity test.
 *
 * Invalidates the settings query on success so the connectivity indicator
 * reflects the probe result.
 */
export function useTestRegistrarConnectivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => adminRegistrarService.testConnectivity(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: registrarKeys.all });
      if (result.isReachable) {
        toast.success("Registrar reachable", {
          description: `${result.provider} responded in ${result.latencyMs}ms`,
        });
      } else {
        toast.error("Registrar unreachable", {
          description: result.detail,
        });
      }
    },
    onError: (error: Error) => {
      toast.error("Connectivity test failed", {
        description: error.message,
      });
    },
  });
}
